import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Plus,
  Search,
  ToggleLeft,
  ToggleRight,
  Pencil,
  Trash2,
  Zap,
  RefreshCw,
} from "lucide-react";
import Swal from "sweetalert2";
import { reglasNotificacionService } from "../../services/api";
import ReglaDrawer from "./ReglaDrawer";

const IMPORTANCIA_STYLES = {
  BAJA: "bg-gray-100 text-gray-600",
  MEDIA: "bg-blue-100 text-blue-700",
  ALTA: "bg-yellow-100 text-yellow-700",
  CRITICA: "bg-red-100 text-red-700",
};

const CANAL_STYLES = {
  EMAIL: "bg-indigo-100 text-indigo-700",
  SMS: "bg-green-100 text-green-700",
  PUSH: "bg-purple-100 text-purple-700",
  WHATSAPP: "bg-emerald-100 text-emerald-700",
};

export default function ReglasList() {
  const { user } = useAuth();
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiposEventos, setTiposEventos] = useState([]);
  const [filters, setFilters] = useState({ tipo_evento: "", canal: "", activo: "" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reglaEditando, setReglaEditando] = useState(null);

  useEffect(() => {
    cargarTipos();
    cargarReglas();
  }, []);

  useEffect(() => {
    cargarReglas();
  }, [filters]);

  const cargarTipos = async () => {
    try {
      const data = await reglasNotificacionService.tiposEventos();
      setTiposEventos(data);
    } catch {}
  };

  const cargarReglas = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tipo_evento) params.tipo_evento = filters.tipo_evento;
      if (filters.canal) params.canal = filters.canal;
      if (filters.activo !== "") params.activo = filters.activo;
      if (user?.empresa_id) params.empresa_id = user.empresa_id;
      const data = await reglasNotificacionService.getAll(params);
      // Estructura: { success, data: { current_page, data: [...] } }
      const items = data?.data?.data ?? data?.data ?? data;
      setReglas(Array.isArray(items) ? items : []);
    } catch {
      setReglas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (regla) => {
    try {
      await reglasNotificacionService.toggleActivo(regla.id);
      setReglas((prev) =>
        prev.map((r) => r.id === regla.id ? { ...r, activo: !r.activo } : r)
      );
    } catch {
      Swal.fire({ icon: "error", title: "Error al cambiar el estado", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    }
  };

  const handleEliminar = async (regla) => {
    const result = await Swal.fire({
      title: "¿Eliminar regla?",
      text: `La regla para "${regla.tipo_evento}" se eliminará permanentemente.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, eliminar",
    });
    if (!result.isConfirmed) return;
    try {
      await reglasNotificacionService.delete(regla.id);
      setReglas((prev) => prev.filter((r) => r.id !== regla.id));
      Swal.fire({ icon: "success", title: "Regla eliminada", toast: true, position: "top-end", showConfirmButton: false, timer: 2500 });
    } catch {
      Swal.fire({ icon: "error", title: "No se pudo eliminar", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    }
  };

  const handleProbar = async (regla) => {
    try {
      const data = await reglasNotificacionService.probar(regla.id);
      const mensaje = data?.data?.mensaje_renderizado ?? JSON.stringify(data, null, 2);
      Swal.fire({
        title: "Vista previa del mensaje",
        html: `<pre style="text-align:left;font-size:13px;white-space:pre-wrap">${mensaje}</pre>`,
        icon: "info",
      });
    } catch (err) {
      Swal.fire({ icon: "warning", title: err.response?.data?.message || "No hay eventos de ejemplo para probar", toast: true, position: "top-end", showConfirmButton: false, timer: 3500 });
    }
  };

  const abrirNueva = () => { setReglaEditando(null); setDrawerOpen(true); };
  const abrirEditar = (regla) => { setReglaEditando(regla); setDrawerOpen(true); };

  const tiposArray = Array.isArray(tiposEventos)
    ? tiposEventos
    : Object.keys(tiposEventos);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reglas de Notificación</h1>
          <p className="text-sm text-gray-500 mt-1">Configurá qué eventos generan notificaciones y a quién.</p>
        </div>
        <button
          onClick={abrirNueva}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={16} /> Nueva regla
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.tipo_evento}
          onChange={(e) => setFilters((f) => ({ ...f, tipo_evento: e.target.value }))}
        >
          <option value="">Todos los eventos</option>
          {tiposArray.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>

        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.canal}
          onChange={(e) => setFilters((f) => ({ ...f, canal: e.target.value }))}
        >
          <option value="">Todos los canales</option>
          <option value="EMAIL">Email</option>
          <option value="SMS">SMS</option>
          <option value="PUSH">Push</option>
          <option value="WHATSAPP">WhatsApp</option>
        </select>

        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.activo}
          onChange={(e) => setFilters((f) => ({ ...f, activo: e.target.value }))}
        >
          <option value="">Todas</option>
          <option value="1">Activas</option>
          <option value="0">Inactivas</option>
        </select>

        <button
          onClick={cargarReglas}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
        >
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 text-center text-gray-400 text-sm">Cargando reglas...</div>
        ) : reglas.length === 0 ? (
          <div className="py-20 text-center">
            <Zap size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No hay reglas configuradas.</p>
            <button onClick={abrirNueva} className="mt-3 text-blue-600 text-sm hover:underline">Crear la primera regla</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Evento</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Canal</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Destinatario</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Importancia</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Activo</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reglas.map((regla) => (
                <tr key={regla.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 font-mono text-gray-700 text-xs">
                    {regla.tipo_evento?.replace(/_/g, " ")}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${CANAL_STYLES[regla.canal] || "bg-gray-100 text-gray-600"}`}>
                      {regla.canal}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{regla.destinatario_tipo}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${IMPORTANCIA_STYLES[regla.importancia] || "bg-gray-100 text-gray-600"}`}>
                      {regla.importancia}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleToggle(regla)} className="inline-flex text-gray-400 hover:text-blue-600 transition">
                      {regla.activo
                        ? <ToggleRight size={24} className="text-blue-600" />
                        : <ToggleLeft size={24} />
                      }
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleProbar(regla)}
                        title="Probar"
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition"
                      >
                        <Zap size={15} />
                      </button>
                      <button
                        onClick={() => abrirEditar(regla)}
                        title="Editar"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => handleEliminar(regla)}
                        title="Eliminar"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ReglaDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        regla={reglaEditando}
        onSaved={cargarReglas}
      />
    </div>
  );
}
